"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

// Types
interface AnalysisResult {
  patient_name: string | null;
  chief_complaint: string | null;
  symptoms: string[];
  diagnosis: string | null;
  prescriptions: string[];
  follow_up: string | null;
  flags: string[];
  summary: string;
  raw_transcription: string;
}

type RecordingState = "idle" | "recording" | "processing" | "ready" | "analyzing";

// Use relative paths for nginx reverse proxy
const getApiBase = () => {
  if (typeof window !== "undefined") {
    const protocol = window.location.protocol;
    const host = window.location.host;
    return `${protocol}//${host}/api`;
  }
  return process.env.NEXT_PUBLIC_API_URL || "http://localhost:8004";
};

const getWsBase = () => {
  if (typeof window !== "undefined") {
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    return `${wsProtocol}//${host}`;
  }
  return process.env.NEXT_PUBLIC_WS_URL || "ws://localhost:8004";
};

// Icons
const MicIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
    />
  </svg>
);

const StopIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

const AlertIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
    />
  </svg>
);

const PillIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const UserIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
    />
  </svg>
);

const ClipboardIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
    />
  </svg>
);

const LiveIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <circle cx="12" cy="12" r="4" />
  </svg>
);

const EditIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
    />
  </svg>
);

const SparklesIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
    />
  </svg>
);

// Waveform animation component
function WaveformVisualizer() {
  return (
    <div className="flex items-center justify-center gap-1 h-10">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="waveform-bar w-1 bg-primary rounded-full"
          style={{ minHeight: "8px" }}
        />
      ))}
    </div>
  );
}

// Loading skeleton
function AnalysisSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="h-5 w-32 animate-shimmer rounded" />
        <div className="h-4 w-full animate-shimmer rounded" />
        <div className="h-4 w-3/4 animate-shimmer rounded" />
      </div>
      <Separator />
      <div className="grid grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 w-20 animate-shimmer rounded" />
            <div className="h-6 w-full animate-shimmer rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DoctorNotesDemo() {
  const [state, setState] = useState<RecordingState>("idle");
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [liveTranscription, setLiveTranscription] = useState("");
  const [editableTranscription, setEditableTranscription] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [audioSize, setAudioSize] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const totalBytesRef = useRef<number>(0);
  const stoppedRef = useRef<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const liveTranscriptRef = useRef<HTMLDivElement | null>(null);

  // Format seconds to MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Start recording with WebSocket streaming
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setAnalysis(null);
      setLiveTranscription("");
      setEditableTranscription("");
      setIsEditing(false);
      setAudioSize(0);
      audioChunksRef.current = [];
      totalBytesRef.current = 0;
      stoppedRef.current = false;

      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
      streamRef.current = stream;

      // Open WebSocket connection
      const ws = new WebSocket(`${getWsBase()}/ws/transcribe`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected");
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("WS message:", data.type, data.text?.substring(0, 50));
          if (data.type === "partial" || data.type === "final") {
            setLiveTranscription(data.text);
          }
        } catch (e) {
          console.error("Failed to parse WS message:", e);
        }
      };

      ws.onerror = (err) => {
        console.error("WebSocket error:", err);
        setError("Connection error. Please try again.");
      };

      ws.onclose = () => {
        console.log("WebSocket closed");
        setIsConnected(false);
      };

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          totalBytesRef.current += event.data.size;
          setAudioSize(totalBytesRef.current);
          
          // Send to WebSocket if connected and not stopped
          if (ws.readyState === WebSocket.OPEN && !stoppedRef.current) {
            event.data.arrayBuffer().then((buffer) => {
              if (ws.readyState === WebSocket.OPEN && !stoppedRef.current) {
                ws.send(buffer);
                console.log(`Sent ${buffer.byteLength} bytes, total: ${totalBytesRef.current}`);
              }
            });
          }
        }
      };

      // Stream audio chunks every 1 second for reliable real-time processing
      mediaRecorder.start(1000);
      mediaRecorderRef.current = mediaRecorder;
      setState("recording");
      setRecordingTime(0);

      // Start timer
      timerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      setError("Microphone access denied. Please allow microphone access.");
      console.error("Failed to start recording:", err);
    }
  }, []);

  // Stop recording (don't analyze automatically)
  const stopRecording = useCallback(async () => {
    if (!mediaRecorderRef.current) return;

    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setState("processing");

    // Stop the media recorder
    const mediaRecorder = mediaRecorderRef.current;
    
    await new Promise<void>((resolve) => {
      mediaRecorder.onstop = () => resolve();
      mediaRecorder.stop();
    });

    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    // Send end signal and wait for final transcription
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const ws = wsRef.current;
      
      // Create promise that resolves when we get the final transcription
      const finalPromise = new Promise<string>((resolve) => {
        const timeout = setTimeout(() => {
          console.log("Timeout waiting for final transcription");
          resolve("");
        }, 30000); // 30 second timeout for long recordings
        
        const originalOnMessage = ws.onmessage;
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log("WS message during stop:", data.type);
            if (data.type === "final") {
              clearTimeout(timeout);
              resolve(data.text || "");
            } else if (data.type === "partial") {
              // Update live transcription while waiting
              setLiveTranscription(data.text);
            }
          } catch (e) {
            console.error("Failed to parse WS message:", e);
          }
        };
      });
      
      // Send end signal
      stoppedRef.current = true;
      ws.send(JSON.stringify({ type: "end" }));
      console.log("Sent end signal, waiting for final transcription...");
      
      // Wait for final transcription
      const finalText = await finalPromise;
      console.log("Got final transcription:", finalText.length, "chars");
      
      // Close WebSocket
      ws.close();
      
      if (finalText && finalText.trim() !== "") {
        setEditableTranscription(finalText);
        setLiveTranscription(finalText);
        setState("ready");
        return;
      }
    }

    // Fall back to file-based transcription if WebSocket failed
    const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
    
    if (audioBlob.size > 0) {
      try {
        console.log("Falling back to file-based transcription");
        const formData = new FormData();
        formData.append("file", audioBlob, "recording.webm");

        const response = await fetch(`${getApiBase()}/transcribe`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`API error: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        setEditableTranscription(result.text);
        setLiveTranscription(result.text);
        setState("ready");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to process audio");
        setState("idle");
        console.error("Processing error:", err);
      }
    } else {
      setError("No audio recorded");
      setState("idle");
    }
  }, []);

  // Analyze the transcription (called when user clicks Analyze button)
  const analyzeTranscription = useCallback(async () => {
    if (!editableTranscription.trim()) {
      setError("No transcription to analyze");
      return;
    }

    setState("analyzing");
    setError(null);

    try {
      const response = await fetch(`${getApiBase()}/analyze?transcription=${encodeURIComponent(editableTranscription)}`, {
        method: "POST",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }

      const result: AnalysisResult = await response.json();
      setAnalysis(result);
      setState("idle");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to analyze transcription");
      setState("ready");
      console.error("Analysis error:", err);
    }
  }, [editableTranscription]);

  // Start new recording (reset everything)
  const startNewRecording = useCallback(() => {
    setAnalysis(null);
    setLiveTranscription("");
    setEditableTranscription("");
    setIsEditing(false);
    setError(null);
    setState("idle");
  }, []);

  // Handle double-click to edit
  const handleDoubleClick = useCallback(() => {
    setIsEditing(true);
    // Focus textarea after state update
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.select();
    }, 0);
  }, []);

  // Handle blur (exit editing)
  const handleBlur = useCallback(() => {
    setIsEditing(false);
  }, []);

  // Auto-scroll live transcription to bottom when new text arrives
  useEffect(() => {
    if (liveTranscriptRef.current && state === "recording") {
      liveTranscriptRef.current.scrollTop = liveTranscriptRef.current.scrollHeight;
    }
  }, [liveTranscription, state]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const isRecording = state === "recording";
  const isProcessing = state === "processing";
  const isReady = state === "ready";
  const isAnalyzing = state === "analyzing";

  return (
    <div className="min-h-screen bg-background">
      {/* Background pattern */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,oklch(0.22_0.04_250),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,oklch(0.20_0.06_180),transparent_50%)]" />
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="container max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <header className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            AI-Powered Medical Transcription
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 bg-gradient-to-b from-foreground to-foreground/70 bg-clip-text text-transparent">
            Doctor Notes Assistant
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Record patient consultations and get instant AI-powered transcription 
            with structured analysis
          </p>
        </header>

        {/* Main Recording Card */}
        <Card className="mb-8 border-border/50 bg-card/80 backdrop-blur-sm shadow-2xl shadow-black/20">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl flex items-center justify-center gap-2">
              Voice Recording
              {isRecording && isConnected && (
                <span className="inline-flex items-center gap-1 text-xs font-normal text-destructive animate-pulse">
                  <LiveIcon className="w-3 h-3" />
                  LIVE
                </span>
              )}
            </CardTitle>
            <CardDescription>
              {isRecording
                ? "Recording in progress — live transcription below"
                : isProcessing
                  ? "Finalizing transcription..."
                  : isReady || isAnalyzing
                    ? "Review and edit transcription, then analyze"
                    : analysis
                      ? "Recording complete — start new to record again"
                      : "Press the button to start recording"}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-6 pt-4">
            {/* Recording visualization */}
            <div className="relative">
              {/* Pulse rings when recording */}
              {isRecording && (
                <>
                  <div className="absolute inset-0 rounded-full bg-destructive/20 animate-pulse-ring" />
                  <div
                    className="absolute inset-0 rounded-full bg-destructive/10 animate-pulse-ring"
                    style={{ animationDelay: "0.5s" }}
                  />
                </>
              )}

              {/* Main button */}
              <Button
                size="lg"
                variant={isRecording ? "destructive" : "default"}
                className={`relative w-24 h-24 rounded-full transition-all duration-300 cursor-pointer ${
                  isRecording
                    ? "bg-destructive hover:bg-destructive/90 scale-110"
                    : "bg-primary hover:bg-primary/90 hover:scale-105"
                } ${isProcessing || isAnalyzing ? "opacity-50 !cursor-not-allowed" : ""}`}
                onClick={isRecording ? stopRecording : (isReady || analysis) ? startNewRecording : startRecording}
                disabled={isProcessing || isAnalyzing}
              >
                {isRecording ? (
                  <StopIcon className="w-10 h-10" />
                ) : (
                  <MicIcon className="w-10 h-10" />
                )}
              </Button>
            </div>

            {/* Recording time */}
            {isRecording && (
              <div className="flex flex-col items-center gap-2">
                <WaveformVisualizer />
                <span className="text-2xl font-mono font-semibold text-foreground">
                  {formatTime(recordingTime)}
                </span>
              </div>
            )}

            {/* Processing indicator */}
            {isProcessing && (
              <div className="flex flex-col items-center gap-3">
                <div className="flex gap-1">
                  <div className="w-3 h-3 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-3 h-3 rounded-full bg-primary animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-3 h-3 rounded-full bg-primary animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
                <span className="text-muted-foreground">
                  Finalizing transcription...
                </span>
              </div>
            )}

            {/* Instructions */}
            {!isRecording && !isProcessing && !isReady && !isAnalyzing && !liveTranscription && !analysis && (
              <p className="text-sm text-muted-foreground text-center max-w-sm">
                Speak naturally about the patient consultation. Include details like
                symptoms, diagnosis, and prescriptions for best results.
              </p>
            )}

            {/* New recording hint */}
            {(isReady || analysis) && (
              <p className="text-sm text-muted-foreground text-center">
                Click the mic button to start a new recording
              </p>
            )}
          </CardContent>
        </Card>

        {/* Live Transcription Card (during recording) */}
        {isRecording && (
          <Card className="mb-8 border-border/50 bg-card/80 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                </span>
                Live Transcription
                <span className="text-xs font-normal text-muted-foreground ml-auto">
                  {(audioSize / 1024).toFixed(0)} KB recorded
                  {audioSize > 20000 && " • Processing..."}
                </span>
              </CardTitle>
              <CardDescription>
                Real-time transcription{audioSize < 20000 ? " (speak for ~1-2 seconds)" : ""}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                ref={liveTranscriptRef}
                className="p-4 rounded-lg bg-muted/50 border border-border/50 min-h-[100px] max-h-[250px] overflow-y-auto scroll-smooth"
              >
                {liveTranscription ? (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {liveTranscription}
                    <span className="inline-block w-2 h-4 ml-1 bg-primary animate-pulse" />
                  </p>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 py-4">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <div className="w-2 h-2 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <div className="w-2 h-2 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {audioSize < 20000 
                        ? "Listening... (speak for ~1-2 seconds)" 
                        : "Processing audio..."}
                    </p>
                  </div>
                )}
        </div>
            </CardContent>
          </Card>
        )}

        {/* Editable Transcription Card (after recording stops) */}
        {(isReady || isAnalyzing) && (
          <Card className="mb-8 border-border/50 bg-card/80 backdrop-blur-sm border-2 border-primary/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <EditIcon className="w-4 h-4 text-primary" />
                Transcription
                {!isEditing && (
                  <span className="text-xs font-normal text-muted-foreground ml-auto">
                    Double-click to edit
                  </span>
                )}
                {isEditing && (
                  <span className="text-xs font-normal text-primary ml-auto">
                    Editing — click outside to save
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                Review and correct any transcription errors before analysis
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Editable text area */}
              <div 
                className={`relative rounded-lg border transition-all duration-200 ${
                  isEditing 
                    ? "bg-background border-primary ring-2 ring-primary/20" 
                    : "bg-muted/50 border-border/50 hover:border-primary/50 cursor-pointer"
                }`}
                onDoubleClick={!isEditing ? handleDoubleClick : undefined}
              >
                {isEditing ? (
                  <textarea
                    ref={textareaRef}
                    value={editableTranscription}
                    onChange={(e) => setEditableTranscription(e.target.value)}
                    onBlur={handleBlur}
                    className="w-full min-h-[150px] p-4 bg-transparent text-sm leading-relaxed resize-y focus:outline-none"
                    placeholder="Enter transcription..."
                  />
                ) : (
                  <div className="p-4 min-h-[150px]">
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {editableTranscription || (
                        <span className="text-muted-foreground italic">
                          No transcription available. Double-click to add text manually.
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Analyze Button */}
              <Button
                size="lg"
                className={`w-full h-14 text-lg font-semibold gap-2 ${isAnalyzing || !editableTranscription.trim() ? "!cursor-not-allowed" : "cursor-pointer"}`}
                onClick={analyzeTranscription}
                disabled={isAnalyzing || !editableTranscription.trim()}
              >
                {isAnalyzing ? (
                  <>
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-primary-foreground animate-bounce" style={{ animationDelay: "0ms" }} />
                      <div className="w-2 h-2 rounded-full bg-primary-foreground animate-bounce" style={{ animationDelay: "150ms" }} />
                      <div className="w-2 h-2 rounded-full bg-primary-foreground animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-5 h-5" />
                    Analyze Transcription
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error display */}
        {error && (
          <Card className="mb-8 border-destructive/50 bg-destructive/10">
            <CardContent className="flex items-center gap-3 py-4">
              <AlertIcon className="w-5 h-5 text-destructive shrink-0" />
              <p className="text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Analysis Results */}
        {analysis && (
          <div className="space-y-6">
            {/* Summary Card */}
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardIcon className="w-5 h-5 text-primary" />
                  Consultation Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Summary text */}
                  <p className="text-lg leading-relaxed">{analysis.summary}</p>

                  <Separator />

                  {/* Key details grid */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Patient Info */}
                    {analysis.patient_name && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                          <UserIcon className="w-4 h-4" />
                          Patient Name
                        </div>
                        <p className="text-lg font-semibold">{analysis.patient_name}</p>
                      </div>
                    )}

                    {/* Chief Complaint */}
                    {analysis.chief_complaint && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                          <AlertIcon className="w-4 h-4" />
                          Chief Complaint
                        </div>
                        <p className="text-lg">{analysis.chief_complaint}</p>
                      </div>
                    )}

                    {/* Diagnosis */}
                    {analysis.diagnosis && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                          <CheckIcon className="w-4 h-4" />
                          Diagnosis
                        </div>
                        <p className="text-lg font-semibold text-primary">
                          {analysis.diagnosis}
                        </p>
                      </div>
                    )}

                    {/* Follow-up */}
                    {analysis.follow_up && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                          <ClipboardIcon className="w-4 h-4" />
                          Follow-up
                        </div>
                        <p className="text-lg">{analysis.follow_up}</p>
                      </div>
                    )}
                  </div>

                  {/* Symptoms */}
                  {analysis.symptoms.length > 0 && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-muted-foreground">
                          Symptoms Reported
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {analysis.symptoms.map((symptom, i) => (
                            <Badge
                              key={i}
                              variant="secondary"
                              className="text-sm py-1.5 px-3"
                            >
                              {symptom}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Prescriptions */}
                  {analysis.prescriptions.length > 0 && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                          <PillIcon className="w-4 h-4" />
                          Prescriptions
                        </h4>
                        <div className="grid gap-2">
                          {analysis.prescriptions.map((rx, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10"
                            >
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                                {i + 1}
                              </div>
                              <span>{rx}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Flags/Alerts */}
                  {analysis.flags.length > 0 && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-destructive flex items-center gap-2">
                          <AlertIcon className="w-4 h-4" />
                          Important Flags
                        </h4>
                        <div className="space-y-2">
                          {analysis.flags.map((flag, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20"
                            >
                              <AlertIcon className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                              <span className="text-destructive">{flag}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Raw Transcription */}
            {analysis.raw_transcription && (
              <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-base">Raw Transcription</CardTitle>
                  <CardDescription>
                    Full text from speech-to-text
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
                    <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
                      {analysis.raw_transcription}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Footer */}
        <footer className="mt-16 text-center text-sm text-muted-foreground">
          <p>
            Powered by{" "}
            <span className="text-primary font-medium">Rezcomm AI Labs</span>
          </p>
        </footer>
        </div>
    </div>
  );
}
